/**
 * OPS-INTRO-001 (Jun 2026) — Operations Dashboard opening animation.
 * 7 rotating CSS/SVG themes + a Performance Moment (real cached metrics only).
 * Deterministic rotation: Mon-Tue / Wed-Thu / Fri-Sun slots x ISO week — every
 * user sees the same theme in the same period. Plays once per browser session,
 * never blocks data loading (pure overlay), honors prefers-reduced-motion.
 * UAT override: ?ops_intro=<theme|next> forces a theme and bypasses the
 * session flag (preview verification only).
 */
import React, { useState, useEffect, useMemo } from 'react';
import './opsIntro.css';

const THEMES = ['orderflow', 'usmap', 'verify', 'network', 'qc', 'property', 'morning', 'performance'];
const SESSION_KEY = 'ops-intro-played';
const STATS_CACHE_KEY = 'ops-stats-cache';
const TOTAL_MS = 1900; // content ~1.6s + 0.3s fade

function isoWeek(d) {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
}

export function pickTheme(now = new Date()) {
  const day = now.getDay(); // 0=Sun..6=Sat
  const slot = (day === 1 || day === 2) ? 0 : (day === 3 || day === 4) ? 1 : 2;
  return THEMES[(isoWeek(now) * 3 + slot) % THEMES.length];
}

function currentUserId() {
  try { return JSON.parse(localStorage.getItem('user') || '{}')?.id || null; } catch { return null; }
}

function readStatsCache() {
  try {
    const raw = localStorage.getItem(STATS_CACHE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (typeof c.total_orders !== 'number' || typeof c.open_orders !== 'number') return null;
    // RBAC guard: cached metrics are role-scoped to whoever fetched them.
    // On shared computers, never show another user's numbers.
    if (!c.user_id || c.user_id !== currentUserId()) return null;
    return c;
  } catch { return null; }
}

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good Morning' : h < 17 ? 'Good Afternoon' : 'Good Evening';
};

/* ── Theme renderers (pure CSS/SVG) ── */

const OrderFlow = () => (
  <div className="opi-stage">
    <div className="opi-flow">
      {['Order Received', 'Assigned', 'Processing', 'QC', 'Completed'].map((s, i) => (
        <React.Fragment key={s}>
          <span className="opi-flow-pill" style={{ animationDelay: `${i * 0.24}s` }}>
            {i === 4 ? '✓ ' : ''}{s}
          </span>
          {i < 4 && <span className="opi-flow-arrow" style={{ animationDelay: `${i * 0.24 + 0.12}s` }}>→</span>}
        </React.Fragment>
      ))}
    </div>
    <p className="opi-caption" style={{ animationDelay: '1.25s' }}>✓ Operations Ready</p>
  </div>
);

const UsMap = () => (
  <div className="opi-stage">
    <svg viewBox="0 0 200 120" className="opi-map" aria-hidden="true">
      <path
        className="opi-map-outline"
        d="M14 38 L30 30 L52 26 L80 24 L110 24 L138 28 L160 24 L178 34 L186 48 L180 62 L184 76 L172 84 L160 80 L150 92 L136 98 L122 92 L104 96 L88 90 L70 88 L54 82 L38 80 L26 68 L18 54 Z"
      />
      {[[46, 46], [76, 40], [108, 42], [140, 44], [62, 66], [96, 66], [128, 70], [156, 58], [36, 58], [148, 84]].map(([x, y], i) => (
        <circle key={i} className="opi-map-dot" cx={x} cy={y} r="3.4" style={{ animationDelay: `${0.18 + i * 0.11}s` }} />
      ))}
    </svg>
    <p className="opi-caption" style={{ animationDelay: '1.2s' }}>Nationwide Operations Active</p>
  </div>
);

const Verify = () => (
  <div className="opi-stage">
    <div className="opi-doc">
      <div className="opi-doc-line" /><div className="opi-doc-line w70" /><div className="opi-doc-line w85" /><div className="opi-doc-line w60" />
      <div className="opi-scanline" />
    </div>
    <div className="opi-verify-steps">
      <span style={{ animationDelay: '0.1s' }}>Searching Records...</span>
      <span style={{ animationDelay: '0.65s' }}>Verifying Data...</span>
      <span className="opi-ok" style={{ animationDelay: '1.2s' }}>✓ Verified</span>
    </div>
  </div>
);

const Network = () => (
  <div className="opi-stage">
    <svg viewBox="0 0 320 90" className="opi-net" aria-hidden="true">
      {[0, 1, 2, 3].map(i => (
        <line key={i} className="opi-net-line" x1={40 + i * 60} y1="34" x2={100 + i * 60} y2="34" style={{ animationDelay: `${0.15 + i * 0.2}s` }} />
      ))}
      {[40, 100, 160, 220, 280].map((x, i) => (
        <circle key={x} className="opi-net-node" cx={x} cy="34" r="9" style={{ animationDelay: `${i * 0.2}s` }} />
      ))}
      <circle className="opi-net-packet" cx="40" cy="34" r="4" />
      {['Order Desk', 'Title', 'Tax', 'QC', 'Mail-Away'].map((l, i) => (
        <text key={l} className="opi-net-label" x={40 + i * 60} y="66" textAnchor="middle" style={{ animationDelay: `${i * 0.2}s` }}>{l}</text>
      ))}
    </svg>
    <p className="opi-caption" style={{ animationDelay: '1.25s' }}>✓ TitleFlow Operations Online</p>
  </div>
);

const Qc = () => (
  <div className="opi-stage">
    <div className="opi-qc-row">
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="opi-qc-card" style={{ animationDelay: `${i * 0.16}s` }}>
          <div className="opi-doc-line" /><div className="opi-doc-line w70" />
          <span className="opi-qc-check" style={{ animationDelay: `${0.35 + i * 0.16}s` }}>✓</span>
        </div>
      ))}
    </div>
    <p className="opi-caption" style={{ animationDelay: '1.2s' }}>Quality Controls Active</p>
  </div>
);

const Property = () => (
  <div className="opi-stage">
    <svg viewBox="0 0 60 52" className="opi-house" aria-hidden="true">
      <path className="opi-house-path" d="M30 6 L54 26 L48 26 L48 46 L12 46 L12 26 L6 26 Z" />
      <rect className="opi-house-door" x="26" y="32" width="8" height="14" rx="1" />
    </svg>
    <div className="opi-flow opi-flow-small">
      {['Property', 'Title Search', 'Tax Search', 'QC', '✓ Complete'].map((s, i) => (
        <React.Fragment key={s}>
          <span className="opi-flow-pill" style={{ animationDelay: `${0.25 + i * 0.2}s` }}>{s}</span>
          {i < 4 && <span className="opi-flow-arrow" style={{ animationDelay: `${0.35 + i * 0.2}s` }}>→</span>}
        </React.Fragment>
      ))}
    </div>
  </div>
);

const Morning = () => (
  <div className="opi-stage">
    <div className="opi-logo">
      <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>
      {[0, 1, 2, 3, 4, 5].map(i => <span key={i} className={`opi-particle opi-p${i}`} />)}
    </div>
    <p className="opi-title" style={{ animationDelay: '0.15s' }}>{greeting()}</p>
    <p className="opi-sub" style={{ animationDelay: '0.5s' }}>Initializing Operations...</p>
    <p className="opi-caption" style={{ animationDelay: '1.15s' }}>✓ Operations Ready</p>
  </div>
);

const Performance = ({ cache }) => (
  <div className="opi-stage">
    <p className="opi-title" style={{ animationDelay: '0.1s' }}>Operations Snapshot</p>
    <div className="opi-perf-row">
      <div className="opi-perf-stat" style={{ animationDelay: '0.4s' }}>
        <span className="opi-perf-num">{cache.total_orders.toLocaleString()}</span>
        <span className="opi-perf-label">Total Orders</span>
      </div>
      <div className="opi-perf-stat" style={{ animationDelay: '0.65s' }}>
        <span className="opi-perf-num">{cache.open_orders.toLocaleString()}</span>
        <span className="opi-perf-label">Open Orders</span>
      </div>
    </div>
    <p className="opi-caption" style={{ animationDelay: '1.2s' }}>✓ Operations Ready</p>
  </div>
);

const RENDERERS = {
  orderflow: OrderFlow, usmap: UsMap, verify: Verify, network: Network,
  qc: Qc, property: Property, morning: Morning, performance: Performance
};

const OpsIntroOverlay = () => {
  const forced = useMemo(() => {
    const p = new URLSearchParams(window.location.search).get('ops_intro');
    return p && (THEMES.includes(p) || p === 'reduced') ? p : null;
  }, []);

  const [visible, setVisible] = useState(() => {
    if (forced) return true;
    return sessionStorage.getItem(SESSION_KEY) !== '1';
  });
  const [fading, setFading] = useState(false);

  const reduced = useMemo(() => (
    forced === 'reduced' ||
    (forced === null && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches)
  ), [forced]);

  const cache = useMemo(readStatsCache, []);
  const theme = useMemo(() => {
    let t = (forced && forced !== 'reduced') ? forced : pickTheme();
    // Performance Moment ONLY with real cached metrics — never fabricate
    if (t === 'performance' && !cache) t = 'morning';
    return t;
  }, [forced, cache]);

  useEffect(() => {
    if (!visible) return;
    sessionStorage.setItem(SESSION_KEY, '1'); // set immediately — once per session
    const total = reduced ? 700 : TOTAL_MS;
    const fadeTimer = setTimeout(() => setFading(true), total - 300);
    const endTimer = setTimeout(() => setVisible(false), total);
    return () => { clearTimeout(fadeTimer); clearTimeout(endTimer); };
  }, [visible, reduced]);

  if (!visible) return null;

  const Renderer = RENDERERS[theme] || Morning;

  return (
    <div className={`opi-overlay ${fading ? 'opi-fade-out' : ''}`} data-testid="ops-intro-overlay" data-theme={reduced ? 'reduced' : theme} aria-hidden="true">
      {reduced ? (
        <div className="opi-stage">
          <p className="opi-caption opi-static">✓ Operations Ready</p>
        </div>
      ) : (
        <Renderer cache={cache || { total_orders: 0, open_orders: 0 }} />
      )}
    </div>
  );
};

export default OpsIntroOverlay;
